import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './LoginPage.css';
import Orb from './Orb';
import TextType from './TextType';
import AppIcon from './icons/AppIcon';

const LoginPage = ({ onLogin }) => {
  const { reloadAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();
  
  // Estado do modal de cadastro
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    nome_empresa: '',
    segmento: '',
    subsegmento: '',
    email: '',
    whatsapp: '',
    senha: '',
    confirmar_senha: '',
    plan_id: '' // Plano selecionado
  });
  const [availablePlans, setAvailablePlans] = useState([]);
  const [registering, setRegistering] = useState(false);

  // Buscar planos disponíveis ao montar o componente
  React.useEffect(() => {
    const fetchPlans = async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('id, name, slug, monthly_credits, price_brl, price_usd')
        .eq('is_active', true)
        .neq('slug', 'admin') // Excluir plano Admin
        .order('display_order');
      
      if (!error && data) {
        setAvailablePlans(data);
      }
    };
    fetchPlans();
  }, []);

  // Opções de segmento
  const SEGMENTOS = [
    { value: '', label: 'Selecione um segmento' },
    { value: 'alimentacao', label: 'Alimentação' },
    { value: 'imoveis', label: 'Imóveis' },
    { value: 'tecnologia', label: 'Tecnologia' },
    { value: 'outros', label: 'Outros' },
  ];

  // Subsegmentos por segmento
  const SUBSEGMENTOS = {
    alimentacao: [
      { value: '', label: 'Selecione o tipo' },
      { value: 'pizzaria', label: 'Pizzaria' },
      { value: 'lanchonete', label: 'Lanchonete' },
      { value: 'hamburgueria', label: 'Hamburgueria' },
      { value: 'comida_oriental', label: 'Comida Oriental' },
      { value: 'restaurante', label: 'Restaurante' },
    ],
    imoveis: [
      { value: '', label: 'Selecione o tipo' },
      { value: 'imobiliaria', label: 'Imobiliária' },
      { value: 'corretor', label: 'Corretor' },
    ],
    tecnologia: [
      { value: '', label: 'Selecione o tipo' },
      { value: 'servicos', label: 'Serviços' },
      { value: 'produtos_geral', label: 'Produtos em Geral' },
    ],
    outros: []
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({
      ...prev,
      [name]: value,
      // Limpar subsegmento se mudar o segmento
      ...(name === 'segmento' ? { subsegmento: '' } : {})
    }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validações
    if (!registerForm.nome_empresa || !registerForm.email || !registerForm.whatsapp || !registerForm.senha) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }

    if (registerForm.senha.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (registerForm.senha !== registerForm.confirmar_senha) {
      setError('As senhas não coincidem.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerForm.email)) {
      setError('E-mail inválido.');
      return;
    }

    setRegistering(true);
    try {
      // 1. Criar usuário no Supabase Auth (sem confirmação de email)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: registerForm.email,
        password: registerForm.senha,
        options: {
          emailRedirectTo: undefined,
          data: {
            nome: registerForm.nome_empresa
          }
        }
      });

      if (authError) throw authError;
      if (!authData?.user) throw new Error('Falha ao criar usuário');

      const userId = authData.user.id;

      // 2. Criar empresa
      const { data: companyData, error: companyError } = await supabase
        .from('configuracoes_empresa')
        .insert({
          company_id: userId, // Usando user_id como company_id
          nome_fantasia: registerForm.nome_empresa,
          email_contato: registerForm.email,
          telefone_principal: registerForm.whatsapp,
          segmento: registerForm.segmento || null,
          subsegmento: registerForm.subsegmento || null,
          plano_contratado: 'GRATUITO',
          status_empresa: 'ATIVO'
        })
        .select()
        .single();

      if (companyError) throw companyError;

      // 3. Buscar plano selecionado ou usar plano gratuito padrão
      let planSlug = null;
      let planIdToUse = registerForm.plan_id;

      // Se nenhum plano foi selecionado, usar plano gratuito padrão
      if (!planIdToUse) {
        const freePlan = availablePlans.find(p => p.slug === 'free');
        if (freePlan) {
          planIdToUse = freePlan.id;
          planSlug = 'free';
        }
      } else {
        const selectedPlan = availablePlans.find(p => p.id === registerForm.plan_id);
        planSlug = selectedPlan?.slug || null;
      }

      // 4. Atualizar profile com company_id e plano
      // ⚠️ IMPORTANTE: Apenas usuários com plano 'admin' devem ser superusuários
      const isAdminPlan = planSlug === 'admin';
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          company_id: userId,
          nome: registerForm.nome_empresa,
          email: registerForm.email,
          telefone: registerForm.whatsapp,
          // Definir nível de acesso baseado no plano
          nivel_acesso: isAdminPlan ? 99 : 1, // 99 = Admin, 1 = Usuário básico
          // Apenas usuários com plano admin são superusuários
          is_superuser: isAdminPlan ? true : false,
          plano_conta: planSlug // Plano selecionado
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // 5. SEMPRE criar assinatura e créditos (com plano padrão se necessário)
      if (planIdToUse) {
        const selectedPlan = availablePlans.find(p => p.id === planIdToUse);
        
        // Criar assinatura
        const { error: subscriptionError } = await supabase
          .from('company_subscriptions')
          .insert({
            company_id: userId,
            plan_id: planIdToUse,
            status: 'active',
            started_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // +30 dias
            auto_renew: true,
            cancel_at_period_end: false
          });

        if (subscriptionError) console.error('Erro ao criar assinatura:', subscriptionError);

        // Criar créditos
        if (selectedPlan) {
          const { error: creditsError } = await supabase
            .from('company_credits')
            .insert({
              company_id: userId,
              credits_available: selectedPlan.monthly_credits,
              credits_used: 0,
              credits_total: selectedPlan.monthly_credits,
              credits_bonus: 0,
              period_start: new Date().toISOString().split('T')[0],
              period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              last_reset_at: new Date().toISOString()
            });

          if (creditsError) console.error('Erro ao criar créditos:', creditsError);
        }
      }

      // Sucesso!
      setShowRegisterModal(false);
      setRegisterForm({
        nome_empresa: '',
        segmento: '',
        subsegmento: '',
        email: '',
        whatsapp: '',
        senha: '',
        confirmar_senha: '',
        plan_id: ''
      });
      
      alert('Cadastro realizado com sucesso! Faça login com seu e-mail e senha.');

    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      const msg = error?.message || 'Erro ao cadastrar empresa.';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('já registrado')) {
        setError('E-mail já está registrado.');
      } else {
        setError(msg);
      }
    } finally {
      setRegistering(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { email, password } = formData;
            
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      // console.log('Resposta Supabase Auth:', { data, authError });

      if (authError) {
        setError(authError.message || 'Falha na autenticação');
      } else {
        // Buscar perfil do usuário no Supabase para montar dados locais mínimos
        try {
          const userId = data?.user?.id;
          const userEmail = data?.user?.email;
          let perfil = null;
          if (userId) {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('id, company_id, nivel_acesso, plano_conta, is_superuser, nome')
              .eq('id', userId)
              .single();
            if (profileError && profileError.code !== 'PGRST116') {
              console.warn('Falha ao carregar perfil do usuário:', profileError);
            } else {
              perfil = profileData || null;
            }
          }
          // Persistir dados mínimos do usuário
          const astroUserData = {
            id: userId,
            email: userEmail,
            company_id: perfil?.company_id || null,
            nivel_acesso: perfil?.nivel_acesso ?? 0,
            plano_conta: perfil?.plano_conta || 'default',
            is_superuser: !!perfil?.is_superuser,
            nome: perfil?.nome || '',
          };
          localStorage.setItem('astro_user_data', JSON.stringify(astroUserData));
          try {
            await Promise.resolve(reloadAuth && reloadAuth());
          } catch (e) {
            console.warn('Falha ao atualizar contexto de auth após login:', e);
          }
        } catch (e) {
        }

        if (onLogin) {
          onLogin({ success: true });
        }
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erro durante o login:', error);
      const errorMsg = error?.response?.data?.message || error.message || 'Ocorreu um erro ao tentar fazer login';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-split-container">
      {/* Lado Esquerdo - Logo */}
      <div className="login-left">
        <div className="login-orb-wrapper login-orb-shift" style={{ width: '100%', height: '600px', position: 'relative' }}>
          <Orb hoverIntensity={1.5} rotateOnHover={true} hue={0} hueShift={270} forceHoverState={false} />
          <div className="login-orb-center">
            <div className="login-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/assets/images/logo-halo-fundo-trasparente.png" 
                alt="Logo Astro CRM" 
                className="logo-image"
                style={{ filter: 'drop-shadow(0 0 14px rgba(0,100,255,0.45))' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const wrap = document.createElement('span');
                  wrap.className = 'login-logo-fallback-svg';
                  wrap.innerHTML =
                    '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/></svg>';
                  wrap.style.color = 'rgba(68,225,255,0.9)';
                  e.target.parentNode.appendChild(wrap);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lado Direito - Formulário */}
      <div className="login-right">
        <div className="login-right-header">
          <TextType
            as="h1"
            className="login-welcome"
            text={["Bem-vindo à HALO IA", "Agentes inteligentes para a sua empresa."]}
            typingSpeed={75}
            pauseDuration={1500}
            deletingSpeed={50}
            cursorBlinkDuration={0.5}
            showCursor={true}
            cursorCharacter="|"
            textColors={["#ffffff"]}
            variableSpeed={{ min: 60, max: 120 }}
          />
        </div>
        <div className="login-card">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">E-mail</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite seu e-mail"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Senha</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Digite sua senha"
                required
                disabled={isLoading}
              />
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? 'Conectando...' : 'Conectar'}
            </button>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <div className="login-footer">
              <span>Não tem uma conta? </span>
              <button 
                type="button"
                className="login-button create-account-button"
                onClick={() => navigate('/register')}
              >
                Cadastrar
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Cadastro */}
      {showRegisterModal && (
        <div className="modal-overlay">
          <div className="modal-content register-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <AppIcon name="business" className="modal-icon" />
                <h2>Criar Nova Conta</h2>
              </div>
              <button 
                className="modal-close-btn"
                onClick={() => !registering && setShowRegisterModal(false)}
                disabled={registering}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <form onSubmit={handleRegisterSubmit} className="register-form">
              <div className="form-group">
                <label htmlFor="nome_empresa">
                  <AppIcon name="store" />
                  Nome da Empresa *
                </label>
                <input
                  type="text"
                  id="nome_empresa"
                  name="nome_empresa"
                  value={registerForm.nome_empresa}
                  onChange={handleRegisterChange}
                  placeholder="Digite o nome da sua empresa"
                  required
                  disabled={registering}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plan_id">
                  <AppIcon name="workspace_premium" />
                  Plano *
                </label>
                <select
                  id="plan_id"
                  name="plan_id"
                  value={registerForm.plan_id}
                  onChange={handleRegisterChange}
                  required
                  disabled={registering}
                  className="plan-select"
                >
                  <option value="">Selecione um plano</option>
                  {availablePlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.monthly_credits.toLocaleString('pt-BR')} créditos/mês
                      {plan.price_brl > 0 ? ` - R$ ${plan.price_brl.toFixed(2)}/mês` : ' - Gratuito'}
                    </option>
                  ))}
                </select>
                <small className="plan-hint">
                  <AppIcon name="info" />
                  Você pode alterar seu plano a qualquer momento
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="segmento">
                    <AppIcon name="category" />
                    Segmento *
                  </label>
                  <select
                    id="segmento"
                    name="segmento"
                    value={registerForm.segmento}
                    onChange={handleRegisterChange}
                    required
                    disabled={registering}
                  >
                    {SEGMENTOS.map(seg => (
                      <option key={seg.value} value={seg.value}>{seg.label}</option>
                    ))}
                  </select>
                </div>

                {registerForm.segmento && SUBSEGMENTOS[registerForm.segmento]?.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="subsegmento">
                      <AppIcon name="label" />
                      Tipo de Negócio
                    </label>
                    <select
                      id="subsegmento"
                      name="subsegmento"
                      value={registerForm.subsegmento}
                      onChange={handleRegisterChange}
                      disabled={registering}
                    >
                      {SUBSEGMENTOS[registerForm.segmento].map(sub => (
                        <option key={sub.value} value={sub.value}>{sub.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <AppIcon name="mail" />
                  E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={registerForm.email}
                  onChange={handleRegisterChange}
                  placeholder="seu@email.com"
                  required
                  disabled={registering}
                />
              </div>

              <div className="form-group">
                <label htmlFor="whatsapp">
                  <AppIcon name="phone" />
                  WhatsApp *
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  value={registerForm.whatsapp}
                  onChange={handleRegisterChange}
                  placeholder="(00) 00000-0000"
                  required
                  disabled={registering}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="senha">
                    <AppIcon name="lock" />
                    Senha *
                  </label>
                  <input
                    type="password"
                    id="senha"
                    name="senha"
                    value={registerForm.senha}
                    onChange={handleRegisterChange}
                    placeholder="Mínimo 6 caracteres"
                    required
                    disabled={registering}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmar_senha">
                    <AppIcon name="lock_reset" />
                    Confirmar Senha *
                  </label>
                  <input
                    type="password"
                    id="confirmar_senha"
                    name="confirmar_senha"
                    value={registerForm.confirmar_senha}
                    onChange={handleRegisterChange}
                    placeholder="Confirme sua senha"
                    required
                    disabled={registering}
                  />
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <AppIcon name="error" />
                  {error}
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowRegisterModal(false)}
                  disabled={registering}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={registering}
                >
                  {registering ? (
                    <>
                      <AppIcon name="progress_activity" className="spinning" />
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <AppIcon name="check" />
                      Criar Conta
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default LoginPage;
